export interface IParsedPastedListItem {
  val:string;
  format?:number;
}

const HEADER_FORMAT = 2;
export const MAX_PASTED_LIST_CHARS = 5000;
const MAX_CLIPBOARD_CHARS = MAX_PASTED_LIST_CHARS;
const MAX_HTML_CHARS = 100000;
const MAX_PASTED_ROWS = 300;
const MAX_DOM_NODES = 2000;
const MAX_DOM_DEPTH = 40;
const MAX_ROW_CHARS = 512;
const MAX_TEXT_READ_CHARS = 2048;

interface IHtmlParseState {
  rows:IParsedPastedListItem[];
  nodesVisited:number;
}

export function parsePastedListItems(plainText:string, htmlText?:string):IParsedPastedListItem[] {
  const safePlainText = safeText(plainText, MAX_CLIPBOARD_CHARS);
  const htmlRows = parseHtmlRows(safeText(htmlText, MAX_HTML_CHARS));
  if (htmlRows.length > 1) {
    return htmlRows;
  }

  return parsePlainTextRows(safePlainText);
}

function parseHtmlRows(htmlText?:string):IParsedPastedListItem[] {
  if (!htmlText || typeof DOMParser === 'undefined') {
    return [];
  }

  const html = htmlText.trim();
  if (html.length === 0) {
    return [];
  }

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.body) {
      return [];
    }

    const state:IHtmlParseState = {rows:[], nodesVisited:0};
    appendHtmlRows(doc.body, state, 0);

    return state.rows;
  } catch (error) {
    return [];
  }
}

function appendHtmlRows(element:Element, state:IHtmlParseState, depth:number):void {
  if (state.rows.length >= MAX_PASTED_ROWS || state.nodesVisited >= MAX_DOM_NODES || depth > MAX_DOM_DEPTH) {
    return;
  }

  state.nodesVisited++;
  const tag = element.tagName.toUpperCase();

  if (tag === 'SCRIPT' || tag === 'STYLE') {
    return;
  }

  if (/^H[1-6]$/.test(tag)) {
    pushRow(state.rows, cleanText(boundedTextContent(element)), HEADER_FORMAT);
    return;
  }

  if (tag === 'LI') {
    pushHtmlListItem(element, state, depth);
    return;
  }

  if (tag === 'UL' || tag === 'OL') {
    appendElementChildren(element, state, depth + 1);
    return;
  }

  if (hasBlockChild(element)) {
    appendElementChildren(element, state, depth + 1);
    return;
  }

  if (tag === 'P' || tag === 'DIV') {
    const text = cleanText(boundedTextContent(element));
    pushRow(state.rows, text, isBoldOnlyBlock(element, text) ? HEADER_FORMAT : undefined);
  }
}

function appendElementChildren(element:Element, state:IHtmlParseState, depth:number):void {
  Array.from(element.children).some((child) => {
    appendHtmlRows(child, state, depth);
    return state.rows.length >= MAX_PASTED_ROWS || state.nodesVisited >= MAX_DOM_NODES;
  });
}

function pushHtmlListItem(element:Element, state:IHtmlParseState, depth:number):void {
  pushRow(state.rows, cleanText(textContentWithoutNestedLists(element, depth)), undefined);

  Array.from(element.children)
    .filter((child) => child.tagName.toUpperCase() === 'UL' || child.tagName.toUpperCase() === 'OL')
    .some((child) => {
      appendHtmlRows(child, state, depth + 1);
      return state.rows.length >= MAX_PASTED_ROWS || state.nodesVisited >= MAX_DOM_NODES;
    });
}

function hasBlockChild(element:Element):boolean {
  return Array.from(element.children).some((child) => {
    const tag = child.tagName.toUpperCase();
    return tag === 'P' || tag === 'DIV' || tag === 'UL' || tag === 'OL' || tag === 'LI' || /^H[1-6]$/.test(tag);
  });
}

function isBoldOnlyBlock(element:Element, text:string):boolean {
  if (text.length === 0 || text.length > 80) {
    return false;
  }

  return Array.from(element.childNodes).every((node) => {
    if (node.nodeType === 3) {
      return cleanText(node.textContent || '').length === 0;
    }
    if (node.nodeType !== 1) {
      return true;
    }

    const tag = (node as Element).tagName.toUpperCase();
    return tag === 'B' || tag === 'STRONG' || tag === 'BR';
  });
}

function parsePlainTextRows(plainText:string):IParsedPastedListItem[] {
  const rawRows = plainText.split(/\r\n|\n|\r/).slice(0, MAX_PASTED_ROWS);
  const markdownish = rawRows.some((row, index) => hasMarkdownSignal(row, rawRows, index));

  if (!markdownish) {
    return rawRows.map((row) => ({val:truncateRow(row)}));
  }

  const rows:IParsedPastedListItem[] = [];
  rawRows.some((row, index) => {
    const parsed = parseMarkdownishLine(row, rawRows, index);
    pushRow(rows, parsed.val, parsed.format);
    return rows.length >= MAX_PASTED_ROWS;
  });

  return rows;
}

function hasMarkdownSignal(row:string, rawRows:string[], index:number):boolean {
  const text = cleanText(row);
  return isMarkdownHeading(text) ||
    isBoldMarkdownHeading(text) ||
    isBulletLine(text) ||
    shouldTreatAsPlainLabelHeader(text, rawRows, index);
}

function parseMarkdownishLine(row:string, rawRows:string[], index:number):IParsedPastedListItem {
  const text = cleanText(row);
  const heading = text.match(/^#{1,6}\s+(.+?)\s*#*$/);
  if (heading) {
    return {val:cleanText(heading[1]), format:HEADER_FORMAT};
  }

  const boldHeading = text.match(/^(\*\*|__)(.+?)\1:?$/);
  if (boldHeading) {
    return {val:cleanText(stripTrailingHeaderColon(boldHeading[2])), format:HEADER_FORMAT};
  }

  if (shouldTreatAsPlainLabelHeader(text, rawRows, index)) {
    return {val:stripTrailingHeaderColon(text), format:HEADER_FORMAT};
  }

  return {val:stripListMarker(text)};
}

function isMarkdownHeading(text:string):boolean {
  return /^#{1,6}\s+\S/.test(text);
}

function isBoldMarkdownHeading(text:string):boolean {
  return /^(\*\*|__).+?\1:?$/.test(text);
}

function shouldTreatAsPlainLabelHeader(text:string, rawRows:string[], index:number):boolean {
  if (text.length === 0 || text.length > 80 || isBulletLine(text)) {
    return false;
  }

  const nextText = nextNonEmptyRow(rawRows, index);
  if (!nextText || !isBulletLine(nextText)) {
    return false;
  }

  return /:$/.test(text) || !/[.!?]$/.test(text);
}

function nextNonEmptyRow(rawRows:string[], index:number):string|undefined {
  for (let nextIndex = index + 1; nextIndex < rawRows.length; nextIndex++) {
    const text = cleanText(rawRows[nextIndex]);
    if (text.length > 0) {
      return text;
    }
  }

  return undefined;
}

function isBulletLine(text:string):boolean {
  return /^([-*+]\s+|\d+[.)]\s+|[•◦▪▫‣]\s*)/.test(text) ||
    /^[-*+]\s+\[[ xX]\]\s+/.test(text);
}

function stripListMarker(text:string):string {
  return cleanText(text
    .replace(/^[-*+]\s+\[[ xX]\]\s+/, '')
    .replace(/^([-*+]\s+|\d+[.)]\s+|[•◦▪▫‣]\s*)/, ''));
}

function stripTrailingHeaderColon(text:string):string {
  return cleanText(text).replace(/\s*:$/, '');
}

function cleanText(text:string):string {
  return text.replace(/\u00a0/g, ' ').replace(/[ \t\f\v]+/g, ' ').trim();
}

function pushRow(rows:IParsedPastedListItem[], val:string, format?:number):void {
  if (rows.length >= MAX_PASTED_ROWS) {
    return;
  }

  const rowVal = truncateRow(format === HEADER_FORMAT ? stripTrailingHeaderColon(val) : val);
  if (rowVal.length === 0) {
    return;
  }

  rows.push(format === undefined ? {val:rowVal} : {val:rowVal, format});
}

function safeText(text:string|undefined|null, maxLength:number):string {
  if (typeof text !== 'string') {
    return '';
  }

  return text.length > maxLength ? text.substring(0, maxLength) : text;
}

function truncateRow(text:string):string {
  return text.length > MAX_ROW_CHARS ? text.substring(0, MAX_ROW_CHARS) : text;
}

function boundedTextContent(element:Element):string {
  const text = element.textContent || '';
  return text.length > MAX_TEXT_READ_CHARS ? text.substring(0, MAX_TEXT_READ_CHARS) : text;
}

function textContentWithoutNestedLists(element:Element, depth:number):string {
  let text = '';

  Array.from(element.childNodes).some((node) => {
    if (text.length >= MAX_TEXT_READ_CHARS || depth > MAX_DOM_DEPTH) {
      return true;
    }

    if (node.nodeType === 3) {
      text += node.textContent || '';
      return text.length >= MAX_TEXT_READ_CHARS;
    }

    if (node.nodeType !== 1) {
      return false;
    }

    const child = node as Element;
    const tag = child.tagName.toUpperCase();
    if (tag === 'UL' || tag === 'OL' || tag === 'SCRIPT' || tag === 'STYLE') {
      return false;
    }

    text += textContentWithoutNestedLists(child, depth + 1);
    return text.length >= MAX_TEXT_READ_CHARS;
  });

  return text.length > MAX_TEXT_READ_CHARS ? text.substring(0, MAX_TEXT_READ_CHARS) : text;
}



// WEBPACK FOOTER //
// ./src/components/sharednotes/parsePastedListItems.ts